import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import CreateTripItem from '../Create'
import SearchAccommodation from '../Search/SearchAccommodation'

import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { CreateTripItemRankSpec } from 'tripPlanner/api/tripItem/types'

interface Props {
  tripId: string
  sectionId?: string
  defaultDate?: string
  rankSpec?: CreateTripItemRankSpec
}

function SelectAccommodationType(props: Props) {
  const { tripId, defaultDate, sectionId, rankSpec } = props

  const intl = useIntl()
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const handleSearchAccommodationClick = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('accommodation_search'))
    openModal(SearchAccommodation, { tripId, defaultDate, sectionId })
  }, [defaultDate, openModal, sectionId, tripId])

  const handleAddCustomAccommodationClick = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('modal_accommodation_custom'))
    openModal(CreateTripItem, {
      itemType: 'ACCOMMODATION' as const,
      defaultDate,
      tripId,
      rankSpec,
    })
  }, [defaultDate, openModal, tripId, rankSpec])

  const searchTitle = intl.formatMessage({
    id: 'Chx6VjiyqqEtf_n9u-UW',
    defaultMessage: 'Search Luxury Escape Offers',
    description: 'List item title for searching accommodation offers in trip planner',
  })
  const searchSubtitle = intl.formatMessage({
    id: 'O6T8Jopq_veMPEBZnjfW',
    defaultMessage: 'Save up to 70% off on exclusive accommodation offers',
    description: 'List item subtitle describing accommodation search discount in trip planner',
  })
  const customTitle = intl.formatMessage({
    id: 'dTda3wDb1cqHdhuYnbnh',
    defaultMessage: 'Add your own accommodation',
    description: 'List item title for adding custom accommodation in trip planner',
  })
  const customSubtitle = intl.formatMessage({
    id: 'qlqXMXk12o_ExycMByoX',
    defaultMessage: 'Enter details of your own booking',
    description: 'List item subtitle for adding custom accommodation booking details in trip planner',
  })

  const listItemsData = useMemo(
    () => [
      {
        startIcon: <LineSearchIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'searchAccommodation',
        title: searchTitle,
        subtitle: searchSubtitle,
        onClick: handleSearchAccommodationClick,
      },
      {
        startIcon: <LineEditAltIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'addCustomAccommodation',
        title: customTitle,
        subtitle: customSubtitle,
        onClick: handleAddCustomAccommodationClick,
      },
    ],
    [handleAddCustomAccommodationClick, handleSearchAccommodationClick, searchTitle, searchSubtitle, customTitle, customSubtitle],
  )

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'TAf3pMFVyRFuvoapIB6t',
          defaultMessage: 'Accommodation',
          description: 'Modal title for accommodation type selection in trip planner',
        })}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          {listItemsData.map((item) => (
            <ClickableListItem
              key={item.key}
              startIcon={item.startIcon}
              endIcon={item.endIcon}
              title={item.title}
              subtitle={item.subtitle}
              onClick={item.onClick}
            />
          ))}
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SelectAccommodationType
